import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { admin } from '../../api/client.js'
import { useApi } from '../../api/useApi.js'
import { btn } from '../../components/admin/ui.jsx'
import { pickI18n } from '../../i18n'
import { pct, timeAgo } from '../../lib.js'
import { ErrorState, Loading } from '../states.jsx'

export function AdminNav() {
  const { t } = useTranslation()
  return (
    <nav className="flex flex-wrap gap-2 text-sm font-extrabold">
      <Link to="/admin" className="rounded-full bg-paper px-4 py-2 text-ink no-underline hover:bg-line">
        {t('admin.feeders')}
      </Link>
      <Link to="/admin/cats" className="rounded-full bg-paper px-4 py-2 text-ink no-underline hover:bg-line">
        {t('admin.cats')}
      </Link>
      <Link to="/admin/health" className="rounded-full bg-paper px-4 py-2 text-ink no-underline hover:bg-line">
        {t('admin.health.title')}
      </Link>
      <Link to="/admin/finance" className="rounded-full bg-paper px-4 py-2 text-ink no-underline hover:bg-line">
        {t('admin.finance.title')}
      </Link>
    </nav>
  )
}

export default function AdminFeedersPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { data, error, loading } = useApi(() => admin.feeders())

  if (loading) return <Loading />
  if (error) return <ErrorState />

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pt-8 pb-16 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-3">
          <h1 className="text-[clamp(28px,3.5vw,40px)] font-black tracking-[-1px]">{t('admin.title')}</h1>
          <AdminNav />
        </div>
        <Link to="/admin/feeders/new" className={`${btn.primary} no-underline`}>
          + {t('admin.newFeeder')}
        </Link>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-line">
        <table className="w-full min-w-[760px] text-left text-[15px]">
          <thead className="bg-paper text-sm text-subtle">
            <tr>
              <th className="px-4 py-3 font-extrabold">{t('admin.feeders')}</th>
              <th className="px-4 py-3 font-extrabold">{t('admin.lastSeen')}</th>
              <th className="px-4 py-3 text-right font-extrabold">{t('admin.battery')}</th>
              <th className="px-4 py-3 text-right font-extrabold">{t('admin.food')}</th>
              <th className="px-4 py-3 text-right font-extrabold">{t('admin.viewers')}</th>
              <th className="px-4 py-3 font-extrabold">{t('admin.camera')}</th>
              <th className="px-4 py-3 font-extrabold">{t('admin.mqtt')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((f) => (
              <tr key={f.id} className="border-t border-line">
                <td className="px-4 py-3">
                  <Link to={`/admin/feeders/${f.slug}`} className="font-extrabold text-ink no-underline hover:text-teal">
                    {pickI18n(f.name, lang)}
                  </Link>
                  <div className="text-xs text-subtle">
                    {f.device_id} · {t(`admin.status.${f.status}`)}
                    {!f.is_public && ' · hidden'}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-2">
                    <span className={`size-2 rounded-full ${f.is_online ? 'bg-teal-soft' : 'bg-faint'}`} />
                    {f.is_online ? t('admin.online') : t('admin.offline')}
                  </span>
                  <span className="text-xs text-subtle">
                    {f.last_seen_at ? timeAgo(f.last_seen_at, lang) : t('admin.never')}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-bold">{pct(f.battery_percent) ?? '—'}</td>
                <td className="px-4 py-3 text-right font-bold">{pct(f.food_level_percent) ?? '—'}</td>
                <td className="px-4 py-3 text-right font-bold">{f.viewers ?? '—'}</td>
                <td className="px-4 py-3">{f.camera_url ? '✓' : t('admin.cameraNone')}</td>
                <td className="px-4 py-3">{f.mqtt_provisioned ? t('admin.mqttYes') : t('admin.mqttNo')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  )
}
